import React from 'react';
import { Link } from 'react-router-dom';
import { Globe2, ShieldCheck, Coins, Truck, ArrowRight, MapPin, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';
import { COUNTRIES } from '../data/constants';
import { ListingGrid } from '../components/ListingGrid';
import { useApi } from '../hooks/useApi';
import { SectionHeading, IconFeature } from '../components/SectionHeading';

const DIASPORA_CODES = ['FR', 'GB', 'US'];

const DiasporaHero = () => {
  const { t } = useApp();
  const countries = COUNTRIES.filter((c) => DIASPORA_CODES.includes(c.code));
  return (
    <section className="relative overflow-hidden bg-[#0F8B5C]" data-testid="diaspora-hero">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #F2B441 0%, transparent 40%)' }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-white">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white text-xs font-semibold uppercase tracking-wider mb-6"><Globe2 className="w-3.5 h-3.5" /> Kushe Diaspora</span>
        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[1.05] max-w-3xl" data-testid="diaspora-title">{t('dia.title')}</h1>
        <p className="mt-6 text-xl text-white/90 max-w-2xl leading-relaxed">{t('dia.subtitle')}</p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          {countries.map((c) => (
            <div key={c.code} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/15">
              <span className="text-2xl">{c.flag}</span><span className="font-semibold">{c.name.replace(' (Diaspora)', '')}</span>
            </div>
          ))}
          <span className="text-sm text-white/70">{t('dia.more')}</span>
        </div>
      </div>
    </section>
  );
};

const InvestSection = () => {
  const { t } = useApp();
  const bullets = [t('dia.l1'), t('dia.l2'), t('dia.l3')];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="bg-[#FAF7F2] rounded-3xl p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#1A1A1A]">{t('dia.invest')}</h2>
          <p className="text-[#3F3A30] mt-4 leading-relaxed">{t('dia.investDesc')}</p>
          <ul className="mt-6 space-y-2 text-[#1A1A1A]">
            {bullets.map((b) => <li key={b} className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#0F8B5C]" /> {b}</li>)}
          </ul>
          <Link to="/marketplace"><Button className="mt-8 bg-[#0F8B5C] hover:bg-[#0A6E47] text-white" data-testid="diaspora-start-button">{t('dia.start')} <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        </div>
        <img src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&q=85" alt="diaspora" className="rounded-2xl aspect-[4/3] object-cover shadow-xl" />
      </div>
    </section>
  );
};

const Diaspora = () => {
  const { t } = useApp();
  const { data: listings, loading, error, reload } = useApi('/listings', { diaspora: true, limit: 8 });
  const features = [
    { id: 'f1', icon: Coins, title: t('dia.f1.t'), desc: t('dia.f1.d') },
    { id: 'f2', icon: Truck, title: t('dia.f2.t'), desc: t('dia.f2.d') },
    { id: 'f3', icon: ShieldCheck, title: t('dia.f3.t'), desc: t('dia.f3.d') },
    { id: 'f4', icon: Sparkles, title: t('dia.f4.t'), desc: t('dia.f4.d') },
  ];

  return (
    <div data-testid="diaspora-page">
      <DiasporaHero />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-4 gap-6">{features.map((f) => <IconFeature key={f.id} {...f} />)}</div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <SectionHeading title={t('dia.products')} linkTo="/marketplace?diaspora=1" linkLabel={t('cat.viewAll')} />
        <ListingGrid listings={listings} loading={loading} error={error} onRetry={reload} testId="diaspora-listings-grid" />
      </section>
      <InvestSection />
    </div>
  );
};

export default Diaspora;
